import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { loadDashboardPage, loadPortalShell } from './app.loaders';
import { authGuard, sessionMatch } from './core/auth/auth.guards';
import { AUTH_ROUTE } from './features/auth/constants/auth-route.constants';

export const routes: Routes = [
  {
    path: 'login',
    loadChildren: () => import('./features/auth/auth.routes').then((m) => m.authRoutes)
  },
  {
    // BRO-1190: the path of the bpp-auth password-reset mail link. It is a backend contract and the
    // only Kundenportal path the mobile app claims (applinks / autoVerify), so it stays a separate
    // path instead of the page's own. A function, because a string redirect drops the query.
    path: 'stella-password-reset',
    redirectTo: ({ queryParams }) => inject(Router).createUrlTree([AUTH_ROUTE.passwordResetConfirm], { queryParams })
  },
  {
    // The portal is the root: its pages are `/overview`, `/contracts`, … with no prefix. `login`
    // is declared above so it still wins the match, and an unknown path falls through to `**`
    // because this empty-path parent only matches when one of its children does.
    path: '',
    loadComponent: loadPortalShell,
    canActivate: [authGuard],
    children: [
      {
        path: 'overview',
        loadComponent: loadDashboardPage
      },
      {
        // A sibling path, not a child of `overview`: the dashboard has no outlet, and the sidebar's
        // `routerLinkActive` (prefix match) keeps "Übersicht" lit on a news article this way.
        path: 'overview/news',
        loadChildren: () => import('./features/news/news.routes').then((m) => m.newsRoutes)
      },
      {
        path: 'contracts',
        loadChildren: () => import('./features/contracts/contracts.routes').then((m) => m.contractsRoutes)
      },
      {
        path: 'products',
        loadChildren: () => import('./features/products/products.routes').then((m) => m.productsRoutes)
      },
      {
        path: 'claims',
        loadChildren: () => import('./features/claims/claims.routes').then((m) => m.claimsRoutes)
      },
      {
        // BRO-1086: the documents the broker delivered. Only in the shell — a delivery belongs to
        // a signed-in person, so unlike `sign` there is no wall twin.
        path: 'documents',
        loadChildren: () => import('./features/documents/documents.routes').then((m) => m.documentsRoutes)
      },
      {
        // BRO-1244: not `annual-figures` — that path belongs to the token wizard, whose `:token`
        // would swallow every sub-route.
        path: 'annual-reviews',
        loadChildren: () => import('./features/annual-reviews/annual-reviews.routes').then((m) => m.annualReviewsRoutes)
      },
      {
        path: 'chat',
        loadChildren: () => import('./features/chat/chat.routes').then((m) => m.chatRoutes)
      },
      {
        path: 'settings',
        loadChildren: () => import('./features/settings/settings.routes').then((m) => m.settingsRoutes)
      },
      {
        // BRO-1177: the Jahreszahlen link is a mail link, so it has to open for a signed-in
        // customer and for an anonymous one. `sessionMatch` answers false for everyone who is
        // not a portal user, which makes the router backtrack out of the shell and fall through
        // to the standalone route below — the same page, without the portal chrome.
        path: 'annual-figures',
        canMatch: [sessionMatch],
        loadChildren: () => import('./features/annual-figures/annual-figures.routes').then((m) => m.annualFiguresRoutes)
      },
      {
        // BRO-1188: the document-request link works the same way — a mail / task link that must
        // open signed in (inside the shell) and signed out (on the wall below).
        path: 'document-upload',
        canMatch: [sessionMatch],
        loadChildren: () => import('./features/document-upload/document-upload.routes').then((m) => m.documentUploadRoutes)
      },
      {
        // BRO-1178: the onboarding invitation is a mail link too. Its reader usually has no account
        // yet, so the wall below is the normal case — the shell is for a customer who is signed in
        // while being onboarded for another customer (e.g. their company).
        path: 'onboarding',
        canMatch: [sessionMatch],
        loadChildren: () => import('./features/onboarding/onboarding.routes').then((m) => m.onboardingRoutes)
      },
      {
        // BRO-1179: signing a document is a mail / task link as well — the one-time token in the
        // link is the credential, so it opens signed in (here) and signed out (on the wall below).
        path: 'sign',
        canMatch: [sessionMatch],
        loadChildren: () => import('./features/document-sign/document-sign.routes').then((m) => m.documentSignRoutes)
      },
      { path: '', pathMatch: 'full', redirectTo: 'overview' }
    ]
  },
  {
    // Must stay below the shell (a portal user gets the chrome) and above `**` (which would
    // swallow the link into the dashboard, i.e. straight back to the login screen).
    path: 'annual-figures',
    loadChildren: () => import('./features/annual-figures/annual-figures.routes').then((m) => m.annualFiguresRoutes),
    data: { wall: true }
  },
  {
    path: 'document-upload',
    loadChildren: () => import('./features/document-upload/document-upload.routes').then((m) => m.documentUploadRoutes),
    data: { wall: true }
  },
  {
    path: 'onboarding',
    loadChildren: () => import('./features/onboarding/onboarding.routes').then((m) => m.onboardingRoutes),
    data: { wall: true }
  },
  {
    path: 'sign',
    loadChildren: () => import('./features/document-sign/document-sign.routes').then((m) => m.documentSignRoutes),
    data: { wall: true }
  },
  { path: '**', redirectTo: 'overview' }
];
